import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Save, User as UserIcon, Mail, Crown } from "lucide-react";

export default function Settings() {
  const { user, revalidate, loading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [picture, setPicture] = useState("");
  const [prime, setPrime] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setPicture(user.picture || "");
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (!user) return;
    api.get("/prime/me").then((r) => setPrime(r.data)).catch(() => {});
  }, [user]);

  const dirty =
    (name || "") !== (user?.name || "") ||
    (picture || "") !== (user?.picture || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", { name, picture: picture || null });
      await revalidate();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white">
      <Nav />
      <div className="pt-32 pb-24 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0]">
            Account
          </div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tighter">
            Settings
          </h1>
          <p className="mt-3 text-[#a0a0a0]">
            Manage your profile, subscription, and account.
          </p>

          {/* Profile */}
          <section
            className="mt-10 squircle border border-[#2b2b2b] bg-[#111] p-6 sm:p-8"
            data-testid="settings-profile-card"
          >
            <div className="flex items-center gap-4">
              <ProfileAvatar user={{ ...user, name, picture }} size={56} />
              <div className="min-w-0">
                <div className="text-lg font-semibold truncate">{name || "Your name"}</div>
                <div className="text-sm text-[#a0a0a0] flex items-center gap-1.5">
                  <Mail size={12} /> {user.email}
                </div>
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <Field label="Display name">
                <input
                  data-testid="settings-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="How the studio addresses you"
                  className="w-full h-11 px-4 rounded-full bg-[#0d0d0d] border border-[#2b2b2b] text-sm focus:outline-none focus:border-[#6589ff]"
                />
              </Field>
              <Field label="Profile picture URL">
                <input
                  data-testid="settings-picture-input"
                  value={picture}
                  onChange={(e) => setPicture(e.target.value)}
                  placeholder="https://…"
                  className="w-full h-11 px-4 rounded-full bg-[#0d0d0d] border border-[#2b2b2b] text-sm focus:outline-none focus:border-[#6589ff]"
                />
              </Field>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={save}
                disabled={!dirty || saving}
                data-testid="settings-save-btn"
                className="btn-accent squircle-sm px-5 h-10 text-sm font-medium btn-anim inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                <Save size={14} /> {saving ? "Saving…" : "Save changes"}
              </button>
              {dirty && (
                <button
                  onClick={() => { setName(user.name || ""); setPicture(user.picture || ""); }}
                  data-testid="settings-cancel-btn"
                  className="squircle-sm border border-[#2b2b2b] px-4 h-10 text-sm hover:bg-[#0d0d0d] btn-anim"
                >
                  Discard
                </button>
              )}
            </div>
          </section>

          {/* Membership */}
          <section
            className="mt-6 squircle border p-6 sm:p-8 flex items-start justify-between gap-4 flex-wrap"
            style={
              prime?.status === "active"
                ? {
                    background: "linear-gradient(180deg, rgba(168,85,247,0.10) 0%, rgba(168,85,247,0.02) 100%)",
                    borderColor: "rgba(168,85,247,0.4)",
                  }
                : { background: "#111", borderColor: "#2b2b2b" }
            }
            data-testid="settings-membership-card"
          >
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0] flex items-center gap-1.5">
                <Crown size={11} style={{ color: "#a855f7" }} /> Membership
              </div>
              <div className="mt-1 text-xl font-semibold">
                {prime?.status === "active"
                  ? prime.plan === "prime_pro"
                    ? "Bliencer Pro Plus"
                    : "Bliencer Pro"
                  : "No active plan"}
              </div>
              <div className="mt-1 text-sm text-[#a0a0a0]">
                {prime?.status === "active"
                  ? `${prime.discount_percentage}% off every order · lifetime saved $${Number(prime.lifetime_savings || 0).toFixed(2)}`
                  : "Join Bliencer Pro to save 15–25% on every order."}
              </div>
            </div>
            <Link
              to="/prime"
              data-testid="settings-manage-plan-btn"
              className="squircle-sm px-5 h-10 text-sm font-medium btn-anim inline-flex items-center gap-2"
              style={{ background: "#a855f7", color: "#1a0d2e" }}
            >
              {prime?.status === "active" ? "Manage plan" : "Explore Prime"} <ArrowRight size={14} />
            </Link>
          </section>

          {/* Sign-in info */}
          <section className="mt-6 squircle border border-[#2b2b2b] bg-[#111] p-6 sm:p-8">
            <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0]">
              Sign-in
            </div>
            <div className="mt-4 text-sm text-[#a0a0a0]">
              You're signed in as <span className="text-white">{user.email}</span>.
              To change your email or password, please <Link to="/contact" className="underline">contact support</Link>.
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0] mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}

/** Shared avatar (initial fallback + picture support) */
export function ProfileAvatar({ user, size = 32 }) {
  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt=""
        className="rounded-full object-cover shrink-0"
        style={{ height: size, width: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full inline-flex items-center justify-center shrink-0 text-white font-semibold"
      style={{
        height: size,
        width: size,
        background: "linear-gradient(135deg, #6589ff 0%, #a855f7 100%)",
        fontSize: size * 0.42,
      }}
    >
      {initial}
    </div>
  );
}
